import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { execute } from '@tutorabc/webview-jsbridge'
import classnames from 'classnames/bind'
import { isEmpty } from 'lodash-es'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import React, { MouseEvent, useEffect, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useMutation, useQueryClient } from 'react-query'
import dynamic from 'next/dynamic'

// Libs
import { setUserRecord } from '@/api/setUserRecord'
import { isLiveMallClub } from '@/constants/systemType'
import useBackRef from '@/hooks/useBackRef'
import useI18n from '@/hooks/useI18n'
import { useLiveRoomListByProductId } from '@/hooks/useLiveRoomListByProductId'
import useMedia from '@/hooks/useMedia'
import { TicketsData, usePromotion } from '@/hooks/usePromotion'
import { usePromotionPriceRange } from '@/hooks/usePromotionPriceRange'
import fastCheckout from '@/methods/fastCheckout'
import getDisplayPrice from '@/methods/getDisplayPrice'
import getDisplaySaleQuantity from '@/methods/getDisplaySaleQuantity'
import { handleCartDataUpdate } from '@/methods/handleCartDataUpdate'
import { handleGtmClickCustomService, handleGtmViewItem } from '@/methods/handleGtmEvent'
import { getVisitorId, recSourceFromQuery } from '@/methods/recommendTracking'
import { recordProductView } from '@/api/recordProductView'
import { handleLoginOrRegister } from '@/methods/handleLoginOrRegister'
import { LineShopEventId, toLineShopProduct, trackLineShopEvent, useLineShopPageView } from '@/methods/lineshop'
import { openFullPagePdf } from '@/methods/livemallclub/openFullPagePdf'
import setBuyNowCacheData from '@/methods/setBuyNowCacheData'
import { toast } from 'react-toastify'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'

// Components
import Icon from '@/assets/icons'
import Button from '@/components/common/Button'
import Drawer from '@/components/common/Drawer'
import Image from '@/components/common/Image'
import Popup from '@/components/common/Popup'
import GoBackButton from '@/components/common/livemallclub/GoBackButton'
import Breadcrumb from '@/components/pages/Product/component/Breadcrumb'
import LiveStreamingPreview from '@/components/pages/Product/component/LiveStreamingPreview'
import MerchantInfo from '@/components/pages/Product/component/MerchantInfo'
import RecommendSection from '@/components/pages/Product/component/RecommendSection'
import GoBackNavi from '@/components/shared/GoBackNavi'
import Insight from '@/components/shared/Insight'
import PurchaseLimitNotice from '@/components/shared/PurchaseLimitNotice'
import { isPurchaseLimitActive } from '@/methods/purchaseLimit'
import { sanitizeHtml } from '@/methods/sanitizeHtml'
import SpecContent from '@/components/shared/SpecContent'
import PriceDisplayer from '../../../../shared/PriceDisplayer'
import Specs from '../../../../shared/Specs'
import ImageList from '../ImageList'
import MainImageSwiper from '../MainImageSwiper'
import MobileActionsFooter from '../MobileActionsFooter'
import Counter from './component/Counter'
import Coupon from './component/Coupon'
import Deliver from './component/Deliver'

// Constants
import { COMMUNICATION_CHANNEL_TYPE } from '@/components/pages/livemallclub/Profile/component/ProfileHeading'
import { BRIDGE_HANDLER } from '@/constants/bridgeHandler'
import { CartAction } from '@/constants/cart'
import { socialReferralCookieKey } from '@/constants/cookieKey'
import { EXTERNAL_LINK } from '@/constants/externalLink'
import { GTM_LOCATION, GtmUpdateCartAction } from '@/constants/gtmEvent'
import { ROOM_LIVE_TYPE } from '@/constants/liveRoom'
import { QUERY_KEY } from '@/constants/queryKey'
import { ROUTE_PATH } from '@/constants/routePath'
import { isClientSideShopApp } from '@/utils/navigator'
import { TERMS_URLS } from '@/constants/livemallclub/termsUrls'

// Methods
import { hasDoubleEleven } from '@/methods/badge/hasDoubleEleven'
import useMerchantPolicies from '@/hooks/livemallclub/useMerchantPolicies'

// Styles
import styles from './style.module.scss'
import 'react-quill-new/dist/quill.snow.css'

const cx = classnames.bind(styles)

// Context
export const liveStreamingPreviewContext = React.createContext({})

const NO_ACTION_FOOTER_SOURCE = new Set(['vendor-profile'])

const ReactQuill = dynamic(() => import('react-quill-new'), { ssr: false })

// 在客戶端註冊自訂字級，避免 SSR 觸發 document 相關錯誤
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { Quill } = require('react-quill-new')
  const SizeStyle = Quill.import('attributors/style/size') as any

  const sizeWhitelist = [
    '12px',
    '14px',
    '16px',
    '18px',
    '20px',
    '24px',
    '32px',
    '40px',
    '48px',
    '56px',
    '64px',
    '72px',
    '80px',
    '88px',
    '96px',
    '104px',
    '112px',
    '120px',
  ]

  SizeStyle.whitelist = sizeWhitelist
  Quill.register(SizeStyle, true)
}

const ValidProduct = (props: any) => {
  const { merchantInfo, productData, isLive, roomId, isMerchantLive, merchantProductList = [], liveProductInfo, liveMerchantInfo } = props

  const { t } = useTranslation()
  const { isOverseaUser } = useI18n()
  const loginPopupRef = useRef<any>(null)
  const [imageIndex, setImageIndex] = useState(0)
  const [productAmount, setProductAmount] = useState(0)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [skuImage, setSkuImage] = useState<string>('')

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [seletcedCoupon, setSelectedCoupon] = useState<TicketsData | undefined>(undefined)

  const router = useRouter()

  const { menu, productInfo, searchKey, filteredItems, handleSearchKeyChange, skuItemInfo, productEntity } = productData
  const { minLivePrice, minOrigPrice, productId, productName, merchantName, categoryName, currency, categoryId } = productInfo

  const fixedPlayerData = useAppSelector((state) => state.common.fixedPlayerData)
  const memberData = useAppSelector((state) => state.member?.memberData)
  const previousPage = useAppSelector((state) => state.common.previousPage)
  // https://tutorabc-org.atlassian.net/browse/SDC-2109
  const { backRefPath, isBackRefResolved } = useBackRef()
  const commonData = useAppSelector((state) => state.common)
  const dittoCurrency = !isEmpty(commonData) && !isEmpty(commonData?.locationData) && commonData?.locationData?._dittoCurrency
  const dispatch = useAppDispatch()

  const memberInfoId = memberData?.memberInfoId
  const isLogin = !isEmpty(memberInfoId)
  const { isPromotionDataSuccess, promotionCouponData } = usePromotion(isLogin, { skuList: [] })
  const haveCouponData = isPromotionDataSuccess && promotionCouponData.length > 0
  const { promotionPriceRangeData } = usePromotionPriceRange({
    couponCodes: seletcedCoupon?.couponCode ? [seletcedCoupon.couponCode] : [],
    skuList: [
      {
        skuId: filteredItems[0]?.skuId,
        quantity: productAmount,
        roomId,
      },
    ],
  })

  const [cookies] = useCookies([socialReferralCookieKey])
  const referralFrom = cookies?.[socialReferralCookieKey]?.socialReferralFrom || ''
  const referralId = cookies?.[socialReferralCookieKey]?.socialReferralId || ''

  const { isDesktop, isMobile } = useMedia()
  const setUserRecordMutation = useMutation(setUserRecord)
  const queryClient = useQueryClient()

  // 加入購物車回報庫存不足時重拉商品資料，讓規格選擇器同步最新庫存
  const handleStockInsufficient = () => queryClient.invalidateQueries(QUERY_KEY.FETCH_PRODUCT_DATA)
  const { liveRoomListDataInstance } = useLiveRoomListByProductId({
    productId,
    liveTypes: `${ROOM_LIVE_TYPE.CURRENT},${ROOM_LIVE_TYPE.FUTURE}`,
  })
  const hideActionFooter = NO_ACTION_FOOTER_SOURCE.has(String(router.query.source))
  const contactUsUrl = (() => {
    if (isLiveMallClub) return ''
    return isOverseaUser ? EXTERNAL_LINK.CUSTOMER_SERVICE_CENTER_HK : EXTERNAL_LINK.CUSTOMER_SERVICE_CENTER
  })()
  const { data: merchantPolicies } = useMerchantPolicies({ merchantId: merchantInfo?.merchantId })
  const returnPolicyUrl = merchantPolicies?.returnPolicyUrl || TERMS_URLS.RETURN_AND_REFUND_POLICY

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen)
  }

  const visitSpecPage = () => {
    handleRoutePushWithQuery({ router, pathname: `${productId}/specs` })
  }

  const currentSku = filteredItems[0]
  // 免運門檻比較用的售價：選定單一 SKU 時用該 SKU 售價，未選定時用最低售價
  const productSalePrice = filteredItems?.length === 1 ? currentSku?.salePrice : productInfo?.minPrice

  useEffect(() => {
    const searchKeyNumberList =
      searchKey
        ?.filter((key: string) => key != null && key !== '')
        .map((key: string) => Number(key))
        .filter((key: number) => !isNaN(key)) || []

    // 如果 sku 只有一個，且有圖片，且選擇的規格數量和 menu 的規格數量相同，則顯示 sku 的圖片
    // 只有一個可購買 SKU 時規格是自動選上的，不算使用者主動選擇，不套規格圖（SDC-2105）
    const hasMultiPurchasableSku = (skuItemInfo?.filter((sku: any) => sku.stockQuantity > 0).length ?? 0) > 1
    const newSkuImage =
      hasMultiPurchasableSku && filteredItems?.length === 1 && !isEmpty(currentSku?.images) && menu.length === searchKeyNumberList.length
        ? currentSku?.images?.[0]
        : ''
    setSkuImage(newSkuImage)
  }, [filteredItems, currentSku])

  useEffect(() => {
    if (!isEmpty(skuImage)) {
      setImageIndex(0)
    }
  }, [skuImage])

  const { displayPrice } = getDisplayPrice({
    isLive,
    origPrice: minOrigPrice,
    minPrice: productInfo.minPrice,
    livePrice: minLivePrice,
  })

  const displaySaleQuantity = getDisplaySaleQuantity(productInfo?.saleQuantity)

  // 隱藏商品（/v1/product/{id} 的 is_hidden）不進 GA 電商數據；LiveMallClub 本來就沒有 GTM_ID
  const isHiddenProduct = productInfo?.isHidden === true

  const gtmUpdateCartData =
    isLiveMallClub || isHiddenProduct
      ? undefined
      : {
          event: GtmUpdateCartAction.ADD,
          currency: currency,
          items: [
            {
              itemName: productName,
              itemId: productId,
              itemBrand: merchantName,
              itemCategory: categoryName,
              itemVarient: currentSku?.attrDescp,
              quantity: productAmount,
              price: displayPrice,
              itemListName: categoryName,
              itemListId: categoryId,
            },
          ],
        }

  const trackAddToCart = () => {
    trackLineShopEvent({
      eventId: LineShopEventId.ADD_TO_CART,
      redirectUrl: typeof window !== 'undefined' ? window.location.href : '',
      products: [
        toLineShopProduct({
          productId,
          productName,
          amount: displayPrice,
          subCategory1: categoryName,
        }),
      ],
    })
  }

  const handleFastCheckout = () => {
    if (!isLiveMallClub && dittoCurrency !== currency) {
      toast.error(`${t('Common_Status_No_Cross_Border_Delivery')}`)
      return
    }

    if (isClientSideShopApp || !isEmpty(memberInfoId)) {
      const fastCheckoutItemPayload = {
        merchantId: productInfo?.merchantId,
        skuId: filteredItems[0]?.skuId,
        quantity: productAmount || 1,
      }
      handleCartDataUpdate({
        t,
        dispatch,
        action: CartAction.ADD,
        onStockInsufficient: handleStockInsufficient,
        skuList: [
          {
            skuId: fastCheckoutItemPayload.skuId,
            quantity: fastCheckoutItemPayload.quantity,
            roomId,
            referralFrom,
            referralId,
          },
        ],
        successCallback: () => fastCheckout({ router, dispatch, fastCheckoutItem: fastCheckoutItemPayload, isLogin: !!memberInfoId }),
        gtmUpdateCartData,
      })
      trackAddToCart()
      setIsDrawerOpen(false)
    } else {
      loginPopupRef.current.setIsPopupOpen(true)
    }
  }

  const handleRedirectToLoginPage = () => {
    setBuyNowCacheData({ productInfo, filteredItems, productAmount: 1, roomId, referralFrom, referralId })
    handleLoginOrRegister({ isLogin: true, router, setUserRecordMutation, fixedPlayerData, gtmLocation: GTM_LOCATION.PRODUCT_ITEM_DETAIL })
  }

  const handleAddToCart = (e: MouseEvent<HTMLButtonElement>) => {
    if (isLiveMallClub) {
      handleRoutePushWithQuery({ router, pathname: `${productId}/specs` })
      return
    }

    if (skuItemInfo?.length === 1) {
      e.stopPropagation()
      handleCartDataUpdate({
        t,
        dispatch,
        action: CartAction.ADD,
        onStockInsufficient: handleStockInsufficient,
        skuList: [
          {
            skuId: filteredItems[0]?.skuId,
            quantity: productAmount || 1,
            roomId,
            referralFrom,
            referralId,
          },
        ],
        gtmUpdateCartData,
      })

      trackAddToCart()
    }
  }

  useEffect(() => {
    if (!isEmpty(productInfo) && !isHiddenProduct) {
      handleGtmViewItem({
        productInfo,
      })
    }
  }, [productInfo, isHiddenProduct])

  // 記錄商品頁瀏覽（最近看過、看了也看、推薦成效）；每個商品進頁一次，失敗不影響商品頁
  useEffect(() => {
    if (!productId || isHiddenProduct || isLiveMallClub) return
    recordProductView({
      productId: Number(productId),
      visitorId: getVisitorId() || undefined,
      recSource: recSourceFromQuery(router.query.rec),
    }).catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isHiddenProduct])

  useLineShopPageView({
    eventId: LineShopEventId.VIEW_PRODUCT,
    enabled: !isEmpty(productInfo) && !!productId,
    products: productId
      ? [
          toLineShopProduct({
            productId,
            productName,
            amount: displayPrice,
            subCategory1: categoryName,
          }),
        ]
      : undefined,
  })

  return (
    <>
      {isLiveMallClub && <GoBackButton onClick={() => router.back()} />}
      <div className={cx('product', { isLiveMallClub })}>
        <div className={cx('container')}>
          {!isLiveMallClub && <Breadcrumb categoryId={categoryId} categoryName={categoryName} rootLabel={t('Header_Mall')} />}
          {!isLiveMallClub && (
            <GoBackNavi
              title={isBackRefResolved ? (backRefPath || previousPage ? t('Common_Back_Previous') : t('Common_Back_Mall')) : ''}
              className={cx('product-back')}
              staticLink={ROUTE_PATH.MALL}
              onClick={backRefPath ? () => router.push(backRefPath) : undefined}
            />
          )}
          <div className={cx('product-info-wrapper')}>
            <div className={cx('product-info')}>
              <div className={cx('product-info-content')}>
                <div className={cx('product-info-image')}>
                  {!isLiveMallClub && isLive && (
                    <Insight
                      liveSourceType={liveProductInfo.liveSourceType}
                      liveType={liveProductInfo.liveType}
                      roomId={roomId}
                      status='live'
                      theme='transparent-black'
                      position='absolute'
                      top='8px'
                      left='8px'
                      zIndex='2'
                    />
                  )}

                  {filteredItems.length >= 0 && (
                    <MainImageSwiper images={productInfo?.images || []} skuImage={skuImage} activeIndex={imageIndex} onSlide={setImageIndex} />
                  )}

                  {!isEmpty(productInfo?.tags) && productInfo?.tags?.length > 0 && hasDoubleEleven(productInfo?.tags) && (
                    <div className={cx('product-info-image__badge-double-11')}>
                      <Image src='/images/badge-double-11.png' fill alt='' />
                    </div>
                  )}

                  {!isEmpty(productInfo?.tags) && productInfo?.tags?.length > 0 && hasDoubleEleven(productInfo?.tags) && (
                    <div className={cx('product-info-image__border-double-11')} />
                  )}
                </div>
                {isEmpty(skuImage) && <ImageList spaceBetween={6} images={productInfo?.images} activeIndex={imageIndex} onClick={setImageIndex} />}
              </div>

              <div className={cx('product-info-specs')}>
                {isDesktop && <div className={cx('product-info-specs-name')}>{productInfo?.productName}</div>}
                {!isLiveMallClub && (
                  <div className={cx('product-info-specs-sale')}>
                    {displaySaleQuantity !== null && (
                      <>
                        <div className={cx('product-info-specs-sale-text')}>{t('Product_Sold', { amount: displaySaleQuantity })}</div>
                      </>
                    )}
                  </div>
                )}

                {!isDesktop && (
                  <div className={cx('product-info-specs-name')}>
                    <span className={cx('product-info-specs-name-text')}>{productInfo?.productName}</span>
                    {isLiveMallClub && isLive && (
                      <Insight
                        liveSourceType={liveProductInfo.liveSourceType}
                        liveType={liveProductInfo.liveType}
                        roomId={roomId}
                        status='live'
                        theme='transparent-black'
                        position='relative'
                        bottom='3px'
                      />
                    )}
                  </div>
                )}

                {isDesktop && (
                  <React.Fragment>
                    <Specs
                      menu={menu}
                      labelClassName={cx('spec-label-text')}
                      filteredItems={filteredItems}
                      searchKeyList={searchKey}
                      skuItemInfo={skuItemInfo}
                      handleSearchKeyChange={handleSearchKeyChange}
                    />

                    <Counter
                      className={cx('product-info-specs-counter')}
                      labelClassName={cx('spec-label-text')}
                      onChange={setProductAmount}
                      maximum={filteredItems.length === 1 ? filteredItems[0].stockQuantity : 0}
                      hidden={filteredItems.length !== 1}
                      isOutOfStock={filteredItems?.length === 0}
                    />
                    {/* 限購僅顯示文案，不影響數量選擇器與加入購物車，判斷點在結帳頁「去付款」 */}
                    <PurchaseLimitNotice className={cx('product-info-specs-purchase-limit')} limitInfo={productInfo} alignWithSpecLabel />
                    <Deliver
                      shippingDays={productInfo?.shippingDays}
                      className={cx('product-info-specs-deliver')}
                      labelClassName={cx('spec-label-text')}
                      merchantId={merchantInfo?.merchantId}
                      logistics={productInfo?.logistics}
                      productPrice={productSalePrice}
                    />
                    {haveCouponData && (
                      <Coupon className={cx('product-info-specs-coupon')} promotionCouponData={promotionCouponData} selectedCoupon={seletcedCoupon} />
                    )}

                    <PriceDisplayer
                      className={cx('product-info-specs-price', { isLiveMallClub })}
                      selectedCoupon={seletcedCoupon}
                      productInfo={productInfo}
                      filteredItems={filteredItems}
                      isLive={isLive}
                      promotionPriceRangeData={promotionPriceRangeData}
                      amount={productAmount}
                      extra={productEntity?.extra}
                    />

                    <div className={cx('product-info-specs-button-wrapper')}>
                      <Button
                        isOutline
                        className={cx('product-info-specs-button-wrapper-button')}
                        disabled={filteredItems.length > 1 || productAmount === 0}
                        onClick={() => {
                          if (dittoCurrency !== currency) {
                            toast.error(`${t('Common_Status_No_Cross_Border_Delivery')}`)
                            return
                          }

                          handleCartDataUpdate({
                            t,
                            dispatch,
                            action: CartAction.ADD,
                            onStockInsufficient: handleStockInsufficient,
                            skuList: [
                              {
                                skuId: filteredItems[0]?.skuId,
                                quantity: productAmount,
                                roomId,
                                referralFrom,
                                referralId,
                              },
                            ],
                            gtmUpdateCartData,
                          })
                          trackAddToCart()
                          setIsDrawerOpen(false)
                        }}
                      >
                        {t('Common_AddToCart')}
                      </Button>
                      <Button
                        className={cx('product-info-specs-button-wrapper-button')}
                        size='lg'
                        onClick={handleFastCheckout}
                        disabled={filteredItems.length > 1 || productAmount === 0}
                      >
                        {t('Common_BuyNow')}
                      </Button>
                    </div>
                  </React.Fragment>
                )}

                {!isDesktop && (
                  <>
                    <PriceDisplayer
                      className={cx('product-info-specs-price')}
                      selectedCoupon={seletcedCoupon}
                      productInfo={productInfo}
                      filteredItems={filteredItems}
                      isLive={isLive}
                      promotionPriceRangeData={promotionPriceRangeData}
                      amount={productAmount}
                      extra={productEntity?.extra}
                    />
                    <PurchaseLimitNotice className={cx('product-info-specs-purchase-limit')} limitInfo={productInfo} />
                    <div className={cx('product-info-drawer')} onClick={isLiveMallClub ? visitSpecPage : toggleDrawer}>
                      <div className={cx('product-info-drawer-label')}>{t('Product_Spec')}</div>
                      <div className={cx('product-info-drawer-action')}>
                        {isLiveMallClub ? t('Product_Spec_Select_LiveMallClub') : t('Product_Spec_Select')}
                      </div>
                      <Icon.ArrowRight />
                    </div>
                  </>
                )}

                <Drawer
                  open={isDrawerOpen && !isDesktop}
                  direction='bottom'
                  onClose={toggleDrawer}
                  lockBackgroundScroll
                  style={{ padding: '0px 0px' }}
                >
                  <div className={cx('product-info-drawer-content')}>
                    <SpecContent
                      menu={menu}
                      filteredItems={filteredItems}
                      searchKeyList={searchKey}
                      skuItemInfo={skuItemInfo}
                      handleSearchKeyChange={handleSearchKeyChange}
                      setProductAmount={setProductAmount}
                      productInfo={productInfo}
                      isLive={isLive}
                      isOutOfStock={filteredItems?.length === 0}
                      productAmount={productAmount}
                      selectedCoupon={seletcedCoupon}
                      promotionPriceRangeData={promotionPriceRangeData}
                    />

                    <div className={cx('product-info-specs-button-wrapper')}>
                      <Button
                        isOutline
                        disabled={filteredItems.length > 1 || productAmount === 0}
                        onClick={() => {
                          if (dittoCurrency !== currency) {
                            toast.error(`${t('Common_Status_No_Cross_Border_Delivery')}`)
                            return
                          }

                          handleCartDataUpdate({
                            t,
                            dispatch,
                            action: CartAction.ADD,
                            onStockInsufficient: handleStockInsufficient,
                            skuList: [
                              {
                                skuId: filteredItems[0]?.skuId,
                                quantity: productAmount,
                                roomId,
                                referralFrom,
                                referralId,
                              },
                            ],
                            gtmUpdateCartData,
                          })
                          trackAddToCart()
                          setIsDrawerOpen(false)
                        }}
                      >
                        {t('Common_AddToCart')}
                      </Button>
                      <Button onClick={handleFastCheckout} disabled={filteredItems.length > 1 || productAmount === 0}>
                        {t('Common_BuyNow')}
                      </Button>
                    </div>
                  </div>
                </Drawer>

                {filteredItems.length === 0 && !isMobile && <div className={cx('product-info-outofstock')}>{t('Product_OutOfStock')}</div>}

                {/* 限購 banner（SDC-2344），放在加入購物車／立即購買下方 */}
                {!isMobile && isPurchaseLimitActive(productInfo) && (
                  <div className={cx('product-info-purchase-limit-banner')}>{t('Product_Purchase_Limit_Banner')}</div>
                )}

                <div className={cx('product-info-specs-tag-wrapper', { isLiveMallClub })}>
                  {!isLiveMallClub && (
                    <div className={cx('product-info-specs-tag-wrapper-item')}>
                      <div className={cx('product-info-specs-tag-wrapper-icon')}>
                        <Icon.Protect />
                      </div>
                      <div className={cx('product-info-specs-tag-wrapper-text')}>{t('Product_Guarantee')}</div>
                    </div>
                  )}
                  {!isLiveMallClub && (
                    <div className={cx('product-info-specs-tag-wrapper-item')}>
                      <div className={cx('product-info-specs-tag-wrapper-icon')}>
                        <Icon.Heart />
                      </div>
                      <div className={cx('product-info-specs-tag-wrapper-text')}>{t('Product_Returns')}</div>
                    </div>
                  )}
                  <div
                    className={cx('product-info-specs-tag-wrapper-item')}
                    onClick={() => {
                      if (isLiveMallClub) {
                        openFullPagePdf(returnPolicyUrl || EXTERNAL_LINK.LIVE_MALL_CLUB_RETURN_POLICY)
                      }
                    }}
                  >
                    <div className={cx('product-info-specs-tag-wrapper-icon', { isLiveMallClub })}>
                      {isLiveMallClub ? <Icon.ThumbUp /> : <Icon.Magnifier />}
                    </div>
                    <div className={cx('product-info-specs-tag-wrapper-text', { isLiveMallClub })}>{t('Product_SevenDays')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!isDesktop && !isLiveMallClub && (
            <>
              <Deliver
                className={cx('product-info-specs-deliver', { 'no-coupon': !haveCouponData })}
                shippingDays={productInfo?.shippingDays}
                merchantId={merchantInfo?.merchantId}
                logistics={productInfo?.logistics}
                productPrice={productSalePrice}
              />
              {haveCouponData && (
                <Coupon className={cx('product-info-specs-coupon')} promotionCouponData={promotionCouponData} selectedCoupon={seletcedCoupon} />
              )}
            </>
          )}

          {/* 商品排播 */}
          {!isLiveMallClub &&
            liveRoomListDataInstance.isSuccess &&
            !isEmpty(liveRoomListDataInstance?.data?.pages) &&
            liveRoomListDataInstance?.data?.pages?.length > 0 &&
            !isEmpty(liveRoomListDataInstance?.data?.pages[0]?.roomList) &&
            liveRoomListDataInstance?.data?.pages[0]?.roomList?.length > 0 && (
              <liveStreamingPreviewContext.Provider value={{ liveRoomListDataInstance }}>
                <LiveStreamingPreview price={minLivePrice} origPrice={minOrigPrice} />
              </liveStreamingPreviewContext.Provider>
            )}
          {/* 商品排播 */}

          {/* 商家資訊 */}
          {merchantInfo?.totalProduct && (
            <MerchantInfo
              isMerchantLive={isMerchantLive}
              roomId={roomId}
              memberInfoId={merchantInfo?.memberInfoId}
              avatar={merchantInfo?.headshotUrl}
              name={merchantInfo?.storeName}
              productTotalNumber={merchantInfo?.totalProduct}
              merchantProductList={merchantProductList}
              liveMerchantInfo={liveMerchantInfo}
              gtmLocation={GTM_LOCATION.PRODUCT_DETAIL}
            />
          )}
          {/* 商家資訊 */}

          {(productEntity?.extra?.attributes || productInfo?.productDetail?.trim().length > 0) && (
            <div className={cx('product-promo')}>
              <div className={cx('product-promo-title')}>{t('Product_Detail')}</div>
            </div>
          )}
          {productEntity?.extra?.attributes && (
            <div className={cx('product-detail')}>
              <ul className={cx('product-detail-content')}>
                {productEntity.extra.attributes.map((attribute: any) => {
                  return (
                    <li key={attribute.attrKey} className={cx('product-detail-item')}>
                      <p className={cx('product-detail-item-title')}>{attribute.attrName}</p>
                      <p className={cx('product-detail-item-description')}>{attribute.attrValue}</p>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          {productInfo?.productDetail?.trim().length > 0 && (
            <div className={cx('product-detail')}>
              <ReactQuill className={cx('quillNoBorder')} value={sanitizeHtml(productInfo?.productDetail)} readOnly modules={{ toolbar: false }} />
            </div>
          )}

          {/* 推薦商品：也買了 / 你可能也喜歡、熱門商品 */}
          {!isLiveMallClub && productInfo?.productId && <RecommendSection productId={productInfo.productId} />}
        </div>

        {filteredItems.length === 0 && isMobile && <div className={cx('product-info-outofstock')}>{t('Product_OutOfStock')}</div>}
        {/* 限購 banner（SDC-2344）：手機版 fixed 在 MobileActionsFooter 上方，桌機／平板版在按鈕下方另外渲染 */}
        {isMobile && isPurchaseLimitActive(productInfo) && (
          <div className={cx('product-info-purchase-limit-banner')}>{t('Product_Purchase_Limit_Banner')}</div>
        )}
        {!isDesktop && !hideActionFooter && (
          <MobileActionsFooter
            storeUrl={`${ROUTE_PATH.USER}/${merchantInfo?.memberInfoId}`}
            contactUsUrl={contactUsUrl}
            onContactUsClick={(e) => {
              if (isLiveMallClub) {
                execute({
                  handlerName: BRIDGE_HANDLER.OPEN_COUMMUNICATION_CHANNEL,
                  data: {
                    type: COMMUNICATION_CHANNEL_TYPE.EMAIL,
                    data: EXTERNAL_LINK.LIVE_MALL_CLUB_SERVICE_EMAIL,
                  },
                })
                return
              } else if (isClientSideShopApp) {
                e.preventDefault()
                execute({
                  handlerName: BRIDGE_HANDLER.OPEN_LINE_OFFICIAL_ACCOUNT,
                })
              }
              handleGtmClickCustomService({ location: GTM_LOCATION.PRODUCT_DETAIL })
            }}
            onButtonWrapperOnClick={() => {
              if (!isLiveMallClub && dittoCurrency !== currency) {
                toast.error(`${t('Common_Status_No_Cross_Border_Delivery')}`)
                return
              }

              if (!isLiveMallClub && skuItemInfo?.length !== 1) {
                toggleDrawer()
              }
            }}
            addToCartButtonProps={{
              disabled: filteredItems.length === 0,
              onClick: handleAddToCart,
            }}
            buyNowButtonProps={{
              disabled: filteredItems.length === 0,
              onClick: handleFastCheckout,
            }}
          />
        )}
      </div>
      <Popup
        ref={loginPopupRef}
        onConfirm={handleRedirectToLoginPage}
        onCancel={() => loginPopupRef.current.setIsPopupOpen(false)}
        title={t('Common_Login_Before_Buy')}
        confirmLabel={t('Common_Login')}
        cancelLabel={t('Common_Cancel')}
      />
    </>
  )
}

export default ValidProduct
